import React, { useEffect, useRef, useState } from 'react';

import Dropdown from '@magoosh/lib/dropdown';

interface Props<T> {
  disabled?: boolean;
  isOptionDisabled?: (option: T) => boolean;
  maxVisibleRows?: number;
  onChange: (option: T) => void;
  options: T[];
  placeholder?: React.ReactNode;
  renderOption: (option: T) => React.ReactNode;
  renderSelected?: (option: T) => React.ReactNode;
  style?: any;
  triggerStyle?: any;
  value?: T;
}

interface OptionsProps<T> {
  close: () => void;
  isExpanded: boolean;
  isOptionDisabled?: (option: T) => boolean;
  maxVisibleRows?: number;
  onChange: (option: T) => void;
  options: T[];
  renderOption: (option: T) => React.ReactNode;
}

function CustomSelectOptions<T>(props: OptionsProps<T>) {
  const optionsRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>();

  useEffect(() => {
    const container = optionsRef.current;
    if (!container) return;

    const measure = () => {
      const rows = Array.from(container.children) as HTMLElement[];
      if (!rows.length) return;

      const heights = rows.map((r) => r.offsetHeight);
      if (heights[0] === 0) return; // dropdown not visible yet

      const totalAllRows = heights.reduce((s, h) => s + h, 0);

      const limitByRows = props.maxVisibleRows
        ? heights.slice(0, props.maxVisibleRows).reduce((s, h) => s + h, 0)
        : totalAllRows;

      setMaxHeight(limitByRows >= totalAllRows ? undefined : limitByRows);
    };

    measure();

    const observer = new ResizeObserver(measure);
    Array.from(container.children).forEach((child) => observer.observe(child));
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [props.maxVisibleRows, props.options]);

  // Each time the dropdown opens, scroll back to the top. Depending on maxHeight as
  // well so we run after the list has been capped to a scrollable height.
  useEffect(() => {
    if (!props.isExpanded) return;
    if (optionsRef.current) optionsRef.current.scrollTop = 0;
  }, [props.isExpanded, maxHeight]);

  return (
    <div
      ref={optionsRef}
      className="d-flex flex-column"
      style={{ maxHeight, overflowY: maxHeight ? 'auto' : undefined }}
    >
      {props.options.map((option, idx) => {
        const disabled = props.isOptionDisabled?.(option);

        return (
          <div
            key={idx}
            onClick={() => {
              if (disabled) return;
              props.onChange(option);
              props.close();
            }}
            style={{ cursor: 'default', opacity: disabled ? 0.5 : 1 }}
          >
            {props.renderOption(option)}
          </div>
        );
      })}
    </div>
  );
}

function CustomSelect<T>(props: Props<T>) {
  const renderSelected = props.renderSelected || props.renderOption;

  return (
    <Dropdown fitViewport>
      <Dropdown.Trigger>
        {({ isExpanded }) => (
          <div className="align-items-center d-flex gap-xs" style={props.triggerStyle}>
            {props.value ? renderSelected(props.value) : props.placeholder}
            <Dropdown.Chevron isExpanded={isExpanded} />
          </div>
        )}
      </Dropdown.Trigger>

      <Dropdown.Content style={props.style}>
        {({ close, isExpanded }) => (
          <CustomSelectOptions
            close={close}
            isExpanded={isExpanded}
            isOptionDisabled={props.isOptionDisabled}
            maxVisibleRows={props.maxVisibleRows}
            onChange={props.onChange}
            options={props.options}
            renderOption={props.renderOption}
          />
        )}
      </Dropdown.Content>
    </Dropdown>
  );
}

export default CustomSelect;
